/**
 * FortifyUI — map-first fortify phase. Overlays (player legend, End Turn
 * button) sit ABOVE the map; the armies stepper + Move button render
 * BELOW the map as an inline panel when a source + ally target are both
 * selected. Nothing floats over territories.
 *
 * Flow:
 *   1. Source candidates (own ≥2 armies + ally neighbor) highlighted by
 *      default. Tap one → selected; ally neighbors highlight.
 *   2. Tap a highlighted ally → action panel below the map shows the
 *      armies stepper + Move button.
 *   3. Confirm → fortify applied; cap default = 1 so the panel typically
 *      clears after one move. Tap End Turn (top-right) to advance.
 *   4. Tap source again, Cancel, or End Turn → done.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView, Platform } from 'react-native'
import { MapView } from '../map/MapView'
import { MapControlsBarSlot } from '../map/MapControlsBar'
import {
  useGameMapSceneRegistration,
  type GameMapSceneRegistration,
} from '../map/gameMapScene'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { useFortify, useEndTurn, useReturnToAttack } from '../../hooks/useGames'
import { turnMapSelection } from '../../lib/turnMapSelection'
import { TurnActionCounter } from './TurnActionCounter'
import { TurnPhaseActionRow } from './TurnPhaseActionRow'
import { TurnControlCard } from './TurnControlCard'
import { GAME_CONTROL_EDGE_INSET } from '../../lib/gameChrome'
import { TURN_CARD_SPACING } from '../../lib/turnPanelAppearance'
import { shouldStackFortifyActions } from '../../lib/phaseActionLayout'
import { isCoarsePointerWeb } from '../../lib/platform'
import type { GameState } from '../../lib/gameState'
import { armyHeadroom } from '@warcarp/api-types'
import { fortifyDestinationIds, fortifySources } from '../../lib/teamPlay'
import type { GameEvent, GamePlayerResponse } from '@warcarp/api-types'
import type { PanOffset } from '../../lib/mapZoom'

interface FortifyUIProps {
  gameId: string
  state: GameState
  myPlayerId: string
  slotByPlayerId: Record<string, number>
  gamePlayers: GamePlayerResponse[]
  viewerUserId?: string
  /** Forwarded to MapView so the player-roster strip pins to the map's
   * right edge and centres on its midpoint (not the screen's). */
  mapRightOverlay?: React.ReactNode
  regionToggleNode?: React.ReactNode
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode?: React.ReactNode
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Optional header rendered at the TOP of the landscape action panel
   * (above the HUD). The game screen colocates the cog + horizontal
   * roster strip here so neither floats over the map.
   */
  actionPanelHeader?: React.ReactNode
  /** Wide layout with persistent chat/log: size the panel to its content
   *  (scrolling past its cap) instead of filling the column. */
  fitContent?: boolean
  /** Thin control bar rendered directly beneath the map (zoom + view mode). */
  mapControlsBar?: React.ReactNode
  /** Forwarded to MapView — zoom-and-pan state managed by the game screen. */
  zoomEnabled?: boolean
  panOffset?: PanOffset
  onPanChange?: (offset: PanOffset) => void
  zoomScale?: number
  onZoomScaleChange?: (scale: number) => void
  /** Forwarded to MapView for bot-attack animation fix (see useAttackEvents). */
  viewerJoinedAt?: number
  /**
   * Game event log — forwarded to MapView so the fortify trail animation
   * layer can detect newly-arrived `fortify` and `post-conquest-transfer`
   * events and draw the teal arrow for fortify-phase moves.
   * Pass `gameState.events` from the fortify-phase branch in ActiveGameView.
   */
  events?: GameEvent[]
  /** Time-machine hook — scrubs the game screen into the snapshot view at the given event index. */
  onViewAtEvent?: (eventIndex: number) => void
  selectedEventIndex?: number | null
  /** When set, the parent owns the single shared MapView. */
  mapSceneKey?: string
  onMapSceneConfigChange?: GameMapSceneRegistration
}

export function FortifyUI({
  gameId,
  state,
  myPlayerId,
  slotByPlayerId,
  gamePlayers,
  viewerUserId,
  mapRightOverlay,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  actionPanelHeader,
  fitContent = false,
  mapControlsBar,
  zoomEnabled,
  panOffset,
  onPanChange,
  zoomScale,
  onZoomScaleChange,
  viewerJoinedAt,
  events,
  selectedEventIndex,
  mapSceneKey,
  onMapSceneConfigChange,
}: FortifyUIProps) {
  const fortify = useFortify(gameId)
  const endTurn = useEndTurn(gameId)
  const returnToAttack = useReturnToAttack(gameId)

  const viewerSlot = slotByPlayerId[myPlayerId] ?? 1
  const currentSlot = parseInt(state.players[state.turnIndex] ?? '0', 10)

  const [selectedFromId, setSelectedFromId] = useState<string | null>(null)
  const [selectedToId, setSelectedToId] = useState<string | null>(null)
  const [armies, setArmies] = useState(1)

  const fortifyCap = state.config.maxFortifiesPerTurn
  const capReached = fortifyCap !== null && state.fortifiesThisTurn >= fortifyCap

  // Sources are always the viewer's own; a destination may be a teammate's in
  // a team game. borderReach = two-way borders + one-way borders, matching
  // the server (lib/teamPlay).
  const sourceCandidates = useMemo(
    () => fortifySources(state, myPlayerId),
    [state, myPlayerId],
  )

  const selectedFrom = selectedFromId
    ? state.map.territories.find((t) => t.id === selectedFromId) ?? null
    : null
  const selectedTo = selectedToId
    ? state.map.territories.find((t) => t.id === selectedToId) ?? null
    : null

  const validTargetIds = useMemo(
    () => fortifyDestinationIds(state, myPlayerId, selectedFrom),
    [selectedFrom, state, myPlayerId],
  )

  // Leave 1 behind, AND respect the destination's troop cap. Uncapped reports
  // Infinity (every territory on every map but Capture the Flag), so this is a
  // no-op there. Without it the slider offers moves the server will reject.
  const maxArmies = selectedFrom
    ? Math.max(1, Math.min(selectedFrom.armies - 1, selectedTo ? armyHeadroom(selectedTo) : Infinity))
    : 1

  // Default the armies stepper to "move all but one" whenever the source
  // changes (or its army count shifts), since that's the typical Risk
  // player intent — fortifying with the largest possible stack. Re-clamp
  // if maxArmies drops below the current value.
  useEffect(() => {
    setArmies(maxArmies)
  }, [selectedFromId, maxArmies])

  // Auto-select target when the chosen source has exactly one valid
  // fortify destination — saves a tap. Skipped when the user has already
  // manually picked a target.
  useEffect(() => {
    if (!selectedFromId || selectedToId) return
    if (validTargetIds.length === 1) {
      setSelectedToId(validTargetIds[0] ?? null)
    }
  }, [selectedFromId, validTargetIds, selectedToId])

  const handleTerritoryPress = useCallback((territoryId: string) => {
    const territory = state.map.territories.find((t) => t.id === territoryId)
    if (!territory) return

    if (territoryId === selectedFromId) {
      setSelectedFromId(null)
      setSelectedToId(null)
      // armies resets via useEffect when selectedFromId clears.
      return
    }

    if (!selectedFromId) {
      if (sourceCandidates.some((s) => s.id === territoryId) && !capReached) {
        setSelectedFromId(territoryId)
        setSelectedToId(null)
        // Cleared armies — useEffect resets to maxArmies once a source is set.
      }
    } else if (validTargetIds.includes(territoryId)) {
      // Don't reset `armies` on target pick — preserve any user-tuned
      // value from the source selection. useEffect already defaulted it
      // to maxArmies when the source was chosen.
      setSelectedToId(territoryId)
    } else if (territory.ownerId === myPlayerId && sourceCandidates.some((s) => s.id === territoryId)) {
      setSelectedFromId(territoryId)
      setSelectedToId(null)
      // useEffect resets armies to the new source's maxArmies.
    } else {
      setSelectedFromId(null)
      setSelectedToId(null)
    }
  }, [state.map.territories, selectedFromId, sourceCandidates, capReached, validTargetIds, myPlayerId])

  const handleBackgroundPress = useCallback(() => {
    setSelectedFromId(null)
    setSelectedToId(null)
  }, [])

  async function handleFortify() {
    if (!selectedFromId || !selectedToId) return
    // Capture cap state before the mutation so we can evaluate it on success
    // without relying on stale closure values from the re-render.
    const wasLastAllowedFortify =
      fortifyCap !== null && state.fortifiesThisTurn + 1 >= fortifyCap
    try {
      await fortify.mutateAsync({
        fromTerritoryId: selectedFromId,
        toTerritoryId: selectedToId,
        armies,
      })
      setSelectedFromId(null)
      setSelectedToId(null)
      // armies resets to 1 via useEffect when selectedFromId clears.

      // Auto-advance when this fortify consumed the last allowed move.
      // When fortifyCap is null (unlimited), the user ends their turn
      // manually via the End Turn button — no auto-advance.
      if (wasLastAllowedFortify) {
        await endTurn.mutateAsync()
      }
    } catch {
      // surfaced inline — do NOT auto-advance on failure
    }
  }

  const canFortify = !!selectedFromId && !!selectedToId && armies >= 1 && !fortify.isPending && !capReached

  // Shared with AttackUI so both phases highlight the map the same way — and so
  // picking a source previews where it can go. No free-fortify interrupt exists
  // in this phase, hence no `freeFortify`. See lib/turnMapSelection.
  const mapSelection = useMemo(
    () =>
      turnMapSelection({
        selectedFromId,
        selectedToId,
        sourceCandidateIds: sourceCandidates.map((t) => t.id),
        targetIds: validTargetIds,
        capReached,
      }),
    [selectedFromId, selectedToId, sourceCandidates, validTargetIds, capReached],
  )

  useGameMapSceneRegistration({
    key: mapSceneKey,
    onChange: onMapSceneConfigChange,
    config: {
      selectedTerritoryId: mapSelection.selectedTerritoryId,
      secondaryTerritoryId: mapSelection.secondaryTerritoryId,
      validTargetIds: mapSelection.validTargetIds,
      previewTargetIds: mapSelection.previewTargetIds,
      selectedFillMode: 'lighten',
      onTerritoryPress: handleTerritoryPress,
      onBackgroundPress: handleBackgroundPress,
    },
  })

  // See AttackUI for the orientation-flip rationale: portrait stacks
  // map + action panel, landscape pins the action panel as a fixed-
  // width sidebar on the left.
  const { isLandscape, isDesktop, actionPanelWidth } = useMapDimensions(state)
  const actionPanel = (
    <ScrollView
      className={fitContent ? undefined : 'flex-1'}
      contentContainerStyle={{ padding: GAME_CONTROL_EDGE_INSET, gap: 8, flexGrow: fitContent ? 0 : 1 }}
      style={isLandscape ? { maxWidth: actionPanelWidth, ...(fitContent ? { flexGrow: 0, flexShrink: 1 } : null) } : undefined}
    >
        {/* Cap row — visible whenever the engagement popup isn't open
            (the popup requires both source AND target). Selecting only
            a source keeps the End Turn button visible so the user can
            always advance unless they're mid-engagement. Close (×) the
            engagement popup to bring the row back. */}
        {!(selectedFromId && selectedToId) &&
          (() => {
            // "Return to Attack" only shows before any fortify this turn.
            const showReturn = state.fortifiesThisTurn === 0
            const stackActions = shouldStackFortifyActions({ isLandscape, isDesktop,
              touchInput: Platform.OS !== 'web' || isCoarsePointerWeb(), showReturn })
            const compact = !stackActions
            const buttonPadX = stackActions ? 12 : 10
            const buttonTextSize = compact ? 10 : 12
            const buttonHeight = compact ? 32 : 36
            const fortLabel = (
              <TurnActionCounter action="fortifies" used={state.fortifiesThisTurn}
                limit={fortifyCap} compact />
            )
            const returnBtn = (
              <TouchableOpacity
                onPress={() => returnToAttack.mutate()}
                disabled={returnToAttack.isPending || endTurn.isPending}
                accessibilityLabel="Return to attack phase"
                accessibilityRole="button"
                className={`border border-olive rounded-md py-1.5 items-center justify-center`}
                style={{ opacity: returnToAttack.isPending ? 0.5 : 1, minHeight: buttonHeight, paddingHorizontal: buttonPadX, ...(stackActions ? { flexGrow: 1 } : {}) }}
              >
                {returnToAttack.isPending ? (
                  <ActivityIndicator size="small" color="#a08050" />
                ) : (
                  <Text className="text-olive font-stencil uppercase" style={{ fontSize: buttonTextSize }} numberOfLines={1}>Return to Attack</Text>
                )}
              </TouchableOpacity>
            )
            const endTurnBtn = (
              <TouchableOpacity
                onPress={() => endTurn.mutate()}
                disabled={endTurn.isPending}
                accessibilityLabel="End turn — next player"
                accessibilityRole="button"
                className={`bg-blood rounded-md py-1.5 items-center justify-center`}
                style={{ opacity: endTurn.isPending ? 0.5 : 1, minHeight: buttonHeight, minWidth: compact ? undefined : 80, paddingHorizontal: buttonPadX, ...(stackActions ? { flexGrow: 1 } : {}) }}
              >
                {endTurn.isPending ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text className="text-white font-stencil uppercase" style={{ fontSize: buttonTextSize }} numberOfLines={1}>End Turn</Text>
                )}
              </TouchableOpacity>
            )
            // The card aligns with the map-bar button edges in portrait;
            // desktop has extra clearance on both sides.
            return (
              <TurnPhaseActionRow compact={compact} stackActions={stackActions} counter={fortLabel}>
                {showReturn ? returnBtn : null}
                {endTurnBtn}
              </TurnPhaseActionRow>
            )
          })()}
        {selectedFrom && selectedTo && (
          <TurnControlCard
            testID="turn-control-card"
            className="gap-2"
            accessibilityLabel={`${selectedFrom.name} → ${selectedTo.name} popup`}
          >
            <View className="flex-row items-center justify-between">
              <Text className="text-ink text-xs font-stencil flex-1 uppercase" numberOfLines={1}>
                {selectedFrom.name} → {selectedTo.name}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  // Clear BOTH so the auto-select-target effect doesn't
                  // immediately re-open the popup when validTargetIds
                  // has exactly one entry (the visible flicker).
                  setSelectedFromId(null)
                  setSelectedToId(null)
                }}
                accessibilityLabel="Close popup"
                accessibilityRole="button"
                className="px-1.5 py-0.5"
              >
                <Text className="text-ink-soft text-base">×</Text>
              </TouchableOpacity>
            </View>

            {/* Min / − / N/max / + / Max — mirrors the free-fortify post-
                conquest panel in AttackUI so the muscle-memory is the same
                between the two flows. "Min" sets to 1 (fortify must move
                ≥1 army; 0 is not valid here, unlike free-fortify's Skip). */}
            <View className="flex-row items-center gap-1">
              <TouchableOpacity
                onPress={() => setArmies(1)}
                disabled={armies === 1 || fortify.isPending}
                accessibilityLabel="Move minimum (1)"
                accessibilityRole="button"
                className="bg-paper border border-olive rounded-md items-center justify-center px-2"
                style={{
                  height: 32,
                  minWidth: 44,
                  opacity: armies === 1 || fortify.isPending ? 0.4 : 1,
                }}
              >
                <Text className="text-ink font-stencil text-xs uppercase">Min</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setArmies((a) => Math.max(1, a - 1))}
                disabled={armies <= 1 || fortify.isPending}
                accessibilityLabel="Decrease armies"
                accessibilityRole="button"
                className="bg-paper border border-olive rounded-md items-center justify-center"
                style={{
                  width: 32,
                  height: 32,
                  opacity: armies <= 1 || fortify.isPending ? 0.4 : 1,
                }}
              >
                <Text className="text-ink font-stencil">−</Text>
              </TouchableOpacity>
              <Text
                className="flex-1 text-center text-ink text-base font-stencil"
                accessibilityLabel={`${armies} armies to move`}
              >
                {armies}
                <Text className="text-ink-soft text-xs font-typed"> / {maxArmies}</Text>
              </Text>
              <TouchableOpacity
                onPress={() => setArmies((a) => Math.min(maxArmies, a + 1))}
                disabled={armies >= maxArmies || fortify.isPending}
                accessibilityLabel="Increase armies"
                accessibilityRole="button"
                className="bg-paper border border-olive rounded-md items-center justify-center"
                style={{
                  width: 32,
                  height: 32,
                  opacity: armies >= maxArmies || fortify.isPending ? 0.4 : 1,
                }}
              >
                <Text className="text-ink font-stencil">+</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setArmies(maxArmies)}
                disabled={armies === maxArmies || fortify.isPending}
                accessibilityLabel="Move all available"
                accessibilityRole="button"
                className="bg-paper border border-olive rounded-md items-center justify-center px-2"
                style={{
                  height: 32,
                  minWidth: 44,
                  opacity: armies === maxArmies || fortify.isPending ? 0.4 : 1,
                }}
              >
                <Text className="text-ink font-stencil text-xs uppercase">All</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              onPress={handleFortify}
              disabled={!canFortify}
              accessibilityLabel="Move armies"
              accessibilityRole="button"
              className="bg-blood rounded-xl py-2.5 items-center flex-row gap-1 justify-center"
              style={{ opacity: canFortify ? 1 : 0.4, minHeight: 44, marginTop: TURN_CARD_SPACING.placementActionsTop }}
            >
              {fortify.isPending && <ActivityIndicator size="small" color="white" />}
              <Text className="text-white font-stencil text-sm uppercase">
                Move {armies} {armies === 1 ? 'army' : 'armies'}
              </Text>
            </TouchableOpacity>
            {fortify.isError && (
              <Text className="text-blood text-[10px] font-typed">
                {fortify.error instanceof Error ? fortify.error.message : 'Fortify failed'}
              </Text>
            )}
          </TurnControlCard>
        )}

        {/* No View Logs button during the fortify phase — the fortify
            controls own the HUD; the log is reachable from the cog menu. */}
      </ScrollView>
  )

  // See AttackUI for the landscape-only centring rationale.
  const mapNode = (
    <MapView
      state={state}
      viewerSlot={viewerSlot}
      selectedTerritoryId={mapSelection.selectedTerritoryId}
      secondaryTerritoryId={mapSelection.secondaryTerritoryId}
      validTargetIds={mapSelection.validTargetIds}
      previewTargetIds={mapSelection.previewTargetIds}
      onTerritoryPress={handleTerritoryPress}
      onBackgroundPress={handleBackgroundPress}
      selectedFillMode="lighten"
      rightOverlay={mapRightOverlay}
      regionToggleNode={regionToggleNode}
      regionToggleCorner={regionToggleCorner}
      zoomToggleNode={zoomToggleNode}
      zoomToggleCorner={zoomToggleCorner}
      events={events}
      hideOnMapZoomButtons={!!mapControlsBar}
      reservedBottom={isLandscape && mapControlsBar ? 44 : undefined}
      zoomEnabled={zoomEnabled}
      panOffset={panOffset}
      onPanChange={onPanChange}
      zoomScale={zoomScale}
      onZoomScaleChange={onZoomScaleChange}
      viewerJoinedAt={viewerJoinedAt}
    />
  )
  if (mapSceneKey && onMapSceneConfigChange) {
    return isLandscape ? (
      <View className="flex-col" style={{ width: actionPanelWidth }}>
        {actionPanelHeader}
        {actionPanel}
      </View>
    ) : (
      actionPanel
    )
  }
  return (
    <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
      {isLandscape && (
        <View className="flex-col" style={{ width: actionPanelWidth }}>
          {actionPanelHeader}
          {actionPanel}
        </View>
      )}
      {isLandscape ? (
        <View className="flex-1 border-l border-olive bg-paper">
          <View className={`${isDesktop ? 'items-start' : 'flex-1 items-start justify-center'}`}>{mapNode}</View>
          <MapControlsBarSlot state={state}>{mapControlsBar}</MapControlsBarSlot>
        </View>
      ) : (
        mapNode
      )}
      {!isLandscape && mapControlsBar}
      {!isLandscape && actionPanel}
    </View>
  )
}
